import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, RefreshCw, Undo2, Ban, User, Clock, ShoppingCart } from "lucide-react";
import { formatDOP } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import OrderNotificationsSettings from "@/components/OrderNotificationsSettings";
import CashierLeaderboard from "@/components/CashierLeaderboard";
import bus from "@/lib/eventBus";
import { EXEMPT_SALE_TYPES } from "@/hooks/useSuggestedNcf";
import OccasionalBuyerDialog from "@/components/OccasionalBuyerDialog";
import { OCCASIONAL_NCF } from "@/lib/occasional";

const CANCEL_REASONS = {
  cliente_arrepentido: "Cliente se arrepintió",
  sin_stock: "Producto sin stock",
  error_vendedor: "Error del vendedor",
  otro: "Otro",
};

export default function SalesOrdersQueue() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pendiente");
  const [ncfTypes, setNcfTypes] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [ncfType, setNcfType] = useState("E32");
  const [ncCode, setNcCode] = useState("");
  const [cancelReason, setCancelReason] = useState("cliente_arrepentido");
  const [cancelText, setCancelText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [occOverride, setOccOverride] = useState(null);
  const [occOpen, setOccOpen] = useState(false);
  const isOcc = !!(occOverride || selected?.occasional_buyer);
  const { can } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sales-orders?status=${statusFilter}`);
      setOrders(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Polling
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Iter 58.2 — Al seleccionar una orden, pre-cargar pago/NCF con los que
  // propuso el vendedor. El cajero puede sobrescribirlos INLINE antes de facturar.
  useEffect(() => {
    if (selected) {
      setPaymentMethod(selected.proposed_payment_method || "efectivo");
      const p = selected.proposed_ncf_type;
      setNcfType(["E31", "E32", "E44", "E45", "E46"].includes(p) ? p : "E32");
      setNcCode("");
      setOccOverride(null);
    }
  }, [selected?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const pickOccasional = (b) => {
    setOccOverride(b);
    if (paymentMethod === "credito") setPaymentMethod("efectivo");
    setNcfType("E31");
  };

  useEffect(() => {
    api.get("/settings/ncf-sequences?usable=true").then(({ data }) => {
      const saleTypes = data.filter((s) =>
        s.active && String(s.type_code || "").startsWith("E") && !["E33", "E34", "E41", "E43", "E47"].includes(s.type_code)
      );
      setNcfTypes(saleTypes);
    }).catch(() => {});
  }, []);

  const doInvoice = async () => {
    if (!selected) return;
    if (!paymentMethod) return toast.error("Selecciona método de pago");
    if (paymentMethod === "nc" && !ncCode.trim()) {
      return toast.error("Ingresa el código de la Nota de Crédito");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/sales-orders/${selected.id}/invoice`, {
        payment_method: paymentMethod,
        ncf_type: ncfType,
        nc_code: paymentMethod === "nc" ? ncCode.toUpperCase() : "",
        ...(occOverride ? {
          occasional_buyer: true, occasional_name: occOverride.name,
          occasional_rnc: occOverride.rnc_cedula, save_occasional: !!occOverride.save_occasional,
        } : {}),
      });
      toast.success(`Factura ${data.number} generada (NCF: ${data.ncf})`);
      setSelected(null);
      load();
      bus.emit("orders:refresh");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const doReturnToCart = async () => {
    if (!selected) return;
    if (!window.confirm(`¿Regresar orden ${selected.number} al carrito editable? El vendedor podrá modificarla nuevamente.`)) return;
    try {
      await api.post(`/sales-orders/${selected.id}/return-to-cart`);
      toast.success("Orden regresada al carrito");
      setSelected(null);
      load();
      bus.emit("orders:refresh");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doCancel = async () => {
    if (!selected) return;
    if (cancelReason === "otro" && !cancelText.trim()) {
      return toast.error("Debes indicar el texto del motivo cuando eliges 'Otro'");
    }
    setSubmitting(true);
    try {
      await api.post(`/sales-orders/${selected.id}/cancel`, {
        reason_code: cancelReason,
        reason_text: cancelText.trim(),
      });
      toast.success("Orden cancelada");
      setShowCancelDialog(false);
      setCancelReason("cliente_arrepentido");
      setCancelText("");
      setSelected(null);
      load();
      bus.emit("orders:refresh");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "ahora";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
  };

  const statusPill = (s) => {
    const map = {
      pendiente: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
      facturada: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300",
      cancelada: "bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300",
      retornada_a_carrito: "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300",
    };
    return <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${map[s] || ""}`}>{s.replace("_", " ")}</span>;
  };

  return (
    <PageContainer>
      <PageHeader
        title="Cola de Órdenes"
        subtitle="Órdenes de venta pendientes de cobro / facturación"
        testid="sales-orders-header"
        actions={
          <div className="flex items-center gap-2">
            <OrderNotificationsSettings />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="facturada">Facturadas</SelectItem>
                <SelectItem value="cancelada">Canceladas</SelectItem>
                <SelectItem value="retornada_a_carrito">Retornadas</SelectItem>
                <SelectItem value="todos">Todas</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} variant="outline" size="sm" data-testid="orders-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        }
      />

      {/* Iter 52 — Dashboard del cajero */}
      <CashierLeaderboard />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.5fr] gap-4">
        {/* Lista de órdenes */}
        <div className="bg-card border rounded-md overflow-hidden">
          <div className="p-3 border-b bg-muted/30 flex items-center justify-between">
            <span className="text-xs font-semibold">{orders.length} órden{orders.length !== 1 && "es"}</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y" data-testid="orders-list">
            {orders.length === 0 && (
              <p className="p-6 text-center text-sm text-muted-foreground">
                No hay órdenes con estado "{statusFilter}".
              </p>
            )}
            {orders.map((o) => {
              const isSelected = selected?.id === o.id;
              return (
                <div
                  key={o.id}
                  onClick={() => setSelected(o)}
                  className={`p-3 cursor-pointer text-sm ${isSelected ? "bg-primary/10" : "hover:bg-accent"}`}
                  data-testid={`order-row-${o.number}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold">{o.number}</span>
                    {statusPill(o.status)}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3" /> {o.customer_name || "Sin cliente"}
                    {o.occasional_buyer && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 uppercase" data-testid={`order-row-occasional-${o.number}`}>Ocasional</span>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      <ShoppingCart className="w-3 h-3" /> Vendedor: {o.vendor_name || "—"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {timeAgo(o.dispatched_at || o.created_at)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-xs">{(o.items || []).length} item{(o.items || []).length !== 1 && "s"}</span>
                    <span className="font-bold text-sm text-primary">{formatDOP(o.total || 0)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detalle */}
        <div className="bg-card border rounded-md">
          {!selected ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              Selecciona una orden de la lista para ver el detalle.
            </p>
          ) : (
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Orden</div>
                  <h2 className="text-2xl font-black" data-testid="order-detail-number">{selected.number}</h2>
                </div>
                {statusPill(selected.status)}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="overline">Cliente</div>
                  <div className="font-semibold" data-testid="order-detail-customer">
                    {isOcc && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 mr-1.5 uppercase" data-testid="order-occasional-badge">Ocasional</span>}
                    {occOverride?.name || selected.customer_name}
                  </div>
                  {(occOverride?.rnc_cedula || selected.customer_rnc_cedula) && (
                    <div className="text-xs text-muted-foreground font-mono">RNC/Céd. {occOverride?.rnc_cedula || selected.customer_rnc_cedula}</div>
                  )}
                  {selected.status === "pendiente" && can("orders.invoice") && (
                    <button type="button" onClick={() => setOccOpen(true)} className="mt-1 text-xs font-semibold text-primary underline"
                      data-testid="order-occasional-btn">
                      {occOverride ? "Cambiar cliente ocasional" : "Facturar a otro RNC o cédula"}
                    </button>
                  )}
                  {occOverride && (
                    <button type="button" onClick={() => {
                      setOccOverride(null);
                      const p = selected.proposed_ncf_type;
                      setNcfType(["E31", "E32", "E44", "E45", "E46"].includes(p) ? p : "E32");
                    }} className="ml-2 text-xs text-muted-foreground underline"
                      data-testid="order-occasional-clear">Quitar</button>
                  )}
                </div>
                <div><div className="overline">Vendedor</div><div className="font-semibold">{selected.vendor_name || "—"}</div></div>
              </div>
              <OccasionalBuyerDialog open={occOpen} onOpenChange={setOccOpen} initial={occOverride} onConfirm={pickOccasional} />

              {/* Iter 58.2 — Selectores editables inline para el cajero.
                  El vendedor sugiere, el cajero DECIDE aquí sin abrir diálogo. */}
              {selected.status === "pendiente" && can("orders.invoice") ? (
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <Label className="overline">
                      Método de pago
                      {selected.proposed_payment_method && (
                        <span className="ml-1 text-[9px] text-muted-foreground normal-case tracking-normal">
                          (vendedor sugirió: {selected.proposed_payment_method})
                        </span>
                      )}
                    </Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="mt-1 font-semibold" data-testid="order-inline-pay-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="transferencia">Transferencia</SelectItem>
                        {!isOcc && <SelectItem value="credito">Crédito</SelectItem>}
                        <SelectItem value="nc">Nota de Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                    {paymentMethod === "nc" && (
                      <Input
                        value={ncCode}
                        onChange={(e) => setNcCode(e.target.value.toUpperCase())}
                        maxLength={8}
                        placeholder="Código NC (8 chars)"
                        className="mt-2 font-mono uppercase tracking-widest text-center"
                        data-testid="order-inline-nc-input"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="overline">
                      Tipo NCF
                      {selected.proposed_ncf_type && (
                        <span className="ml-1 text-[9px] text-muted-foreground normal-case tracking-normal">
                          (vendedor sugirió: {selected.proposed_ncf_type})
                        </span>
                      )}
                    </Label>
                    <Select value={ncfType} onValueChange={setNcfType}>
                      <SelectTrigger className="mt-1 font-semibold" data-testid="order-inline-ncf-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ncfTypes.filter((s) => !isOcc || OCCASIONAL_NCF.includes(s.type_code)).map((s) => (
                          <SelectItem key={s.id} value={s.type_code}>
                            {s.type_code} — {s.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="overline">Pago propuesto</div><div className="font-semibold uppercase">{selected.proposed_payment_method}</div></div>
                  <div><div className="overline">NCF propuesto</div><div className="font-semibold">{selected.proposed_ncf_type}</div></div>
                </div>
              )}

              {selected.notes && (
                <div><div className="overline">Notas</div><div className="text-sm">{selected.notes}</div></div>
              )}
              {selected.status === "cancelada" && (
                <div className="p-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded text-xs">
                  <b>Cancelada</b>: {CANCEL_REASONS[selected.cancellation_reason_code] || selected.cancellation_reason_code}
                  {selected.cancellation_reason_text && ` — ${selected.cancellation_reason_text}`}
                  <br />Por: {selected.cancelled_by}
                </div>
              )}
              {selected.status === "facturada" && (
                <div className="p-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded text-xs">
                  <b>Facturada</b>: {selected.invoice_number} · Cajero: {selected.cashier_name}
                </div>
              )}

              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm" data-testid="order-items-table">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-2 text-left overline">Producto</th>
                      <th className="p-2 text-right overline">Cant.</th>
                      <th className="p-2 text-right overline">Precio</th>
                      <th className="p-2 text-right overline">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.items || []).map((it, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2">
                          <div className="font-medium">{it.name}</div>
                          {it.sku && <div className="text-[10px] text-muted-foreground">{it.sku}</div>}
                        </td>
                        <td className="p-2 text-right">{it.quantity}</td>
                        <td className="p-2 text-right">{formatDOP(it.price)}</td>
                        <td className="p-2 text-right font-semibold">{formatDOP(it.quantity * it.price - (it.discount || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-1 text-sm border-t pt-3">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatDOP(selected.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">ITBIS</span><span data-testid="order-itbis">{formatDOP(EXEMPT_SALE_TYPES.includes(ncfType) ? 0 : selected.itbis)}</span></div>
                <div className="flex justify-between text-lg font-black pt-1 border-t"><span>Total</span><span className="text-primary" data-testid="order-total">{formatDOP(EXEMPT_SALE_TYPES.includes(ncfType) ? selected.subtotal : selected.total)}</span></div>
                {EXEMPT_SALE_TYPES.includes(ncfType) && <p className="text-[11px] text-primary">{ncfType} va sin ITBIS (exento).</p>}
              </div>

              {selected.status === "pendiente" && (
                <div className="grid grid-cols-3 gap-2 pt-3">
                  {can("orders.return_to_cart") && (
                    <Button onClick={doReturnToCart} variant="outline" data-testid="order-return-btn">
                      <Undo2 className="w-4 h-4 mr-1" /> Regresar
                    </Button>
                  )}
                  {can("orders.cancel") && (
                    <Button onClick={() => setShowCancelDialog(true)} variant="outline" className="text-red-600 border-red-300" data-testid="order-cancel-btn">
                      <Ban className="w-4 h-4 mr-1" /> Cancelar
                    </Button>
                  )}
                  {can("orders.invoice") && (
                    <Button
                      onClick={doInvoice}
                      disabled={submitting || !paymentMethod}
                      className="bg-primary font-bold"
                      data-testid="order-invoice-btn"
                    >
                      <Receipt className="w-4 h-4 mr-1" />
                      {submitting ? "Facturando…" : "Facturar"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dialog Cancelar */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar orden {selected?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Motivo *</Label>
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger className="mt-1" data-testid="cancel-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CANCEL_REASONS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Detalle {cancelReason === "otro" && <span className="text-destructive">*</span>}</Label>
              <Input value={cancelText} onChange={(e) => setCancelText(e.target.value)} placeholder="Detalle opcional" className="mt-1" data-testid="cancel-reason-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>Volver</Button>
            <Button onClick={doCancel} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm">
              {submitting ? "Cancelando…" : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
